import {
  Dialog,
  DialogContent,
  Typography,
  DialogActions,
} from '@mui/material';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { SVLoader, SVButton } from '../core';
import { useDeactivateStaff } from '@/hooks/requests/useStaff';

type StaffDeactivateModalProps = {
  id: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function StaffDeactivateModal({
  id,
  open,
  setOpen,
}: StaffDeactivateModalProps) {
  const {
    canDeactivateStaff,
    isCanDeactivateStaffPending,
    deactivateStaff,
    isDeactivateStaffPending,
  } = useDeactivateStaff(id, open);

  if (isCanDeactivateStaffPending) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <DialogContent>
          <SVLoader />
        </DialogContent>
      </Dialog>
    );
  }

  if (!canDeactivateStaff) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <DialogContent>
          <Typography variant="h4">Cannot Deactivate Staff Member</Typography>
          <Typography>
            This staff member cannot be deactivated as they are tied to at least
            one active company as the relationship manager.
          </Typography>
        </DialogContent>
        <DialogActions>
          <SVButton variant="outlined" onClick={() => setOpen(false)}>
            Back
          </SVButton>
        </DialogActions>
      </Dialog>
    );
  }

  async function handleDeactivate() {
    try {
      await deactivateStaff();
      showToast({
        type: 'success',
        data: { content: 'Staff member has been successfully deactivated' },
      });
      setOpen(false);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={() => setOpen(false)}>
      <DialogContent>
        <Typography variant="h4">Deactivate Staff Member?</Typography>
        <Typography>
          Are you sure you would like to deactivate this staff member?
        </Typography>
      </DialogContent>
      <DialogActions>
        <SVButton variant="outlined" onClick={() => setOpen(false)}>
          No, Don't Deactivate
        </SVButton>
        <SVButton
          variant="error"
          isLoading={isDeactivateStaffPending}
          onClick={handleDeactivate}>
          Yes, Deactivate
        </SVButton>
      </DialogActions>
    </Dialog>
  );
}
